import React, { useState, useEffect } from "react";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Typography,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
} from "@mui/material";
import {
  User,
  MapPin,
  Phone,
  GraduationCap,
  Briefcase,
  UserPlus,
} from "lucide-react";
import { Close } from "@mui/icons-material";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import axios from "axios";
import ApplicationSearchBar from "./ApplicationSearchBar";
import { styled } from "@mui/material/styles";
const apiUrl = process.env.REACT_APP_API_URL;

const TruncatedContent = ({ content, maxLength = 10, openDialog }) => {
  if (!content) return "-";

  let cleanedContent = content;
  if (
    typeof content === "string" &&
    content.startsWith("[") &&
    content.endsWith("]")
  ) {
    cleanedContent = content
      .replace(/^\[|\]$/g, "")
      .replace(/\[|\]/g, "")
      .replace(/"/g, "")
      .replace(/'/g, "");
  }

  if (cleanedContent.length <= maxLength) return cleanedContent;

  return (
    <Typography
      component="span"
      sx={{
        cursor: "pointer",
        color: "primary.main",
        "&:hover": { color: "primary.dark" },
        padding: "4px",
        fontSize: "1.1rem",
      }}
      onClick={() =>
        openDialog(
          content.includes("education")
            ? "Education"
            : content.includes("experience")
            ? "Experience"
            : content.includes("profil")
            ? "Profile"
            : "Details",
          cleanedContent
        )
      }
    >
      {cleanedContent.slice(0, maxLength) + "..."}
    </Typography>
  );
};

const SkillChips = ({ skills, openDialog, chipFontSize }) => {
  let skillsArray = [];

  if (Array.isArray(skills)) {
    skillsArray = skills;
  } else if (typeof skills === "string") {
    if (skills.startsWith("[") && skills.endsWith("]")) {
      const cleanedString = skills.replace(/^\[|\]$/g, "").replace(/'/g, '"');
      skillsArray = cleanedString
        .split(/,\s*/)
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
    } else {
      skillsArray = skills.split(",").map((s) => s.trim());
    }
  }

  const cleanSkillsArray = skillsArray
    .map((skill) =>
      typeof skill === "string"
        ? skill.trim().replace(/[\[\]'"\s]+/g, "")
        : String(skill).trim()
    )
    .filter((s) => s.length > 0);

  const displaySkills = cleanSkillsArray.slice(0, 3);
  const remainingCount = cleanSkillsArray.length - 3;
  const allSkillsFormatted = cleanSkillsArray.join(", ");

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
      {displaySkills.map((skill, index) => (
        <Chip
          key={index}
          label={skill}
          size="small"
          sx={{
            backgroundColor: "#f0f9ff",
            color: "#0369a1",
            "&:hover": { backgroundColor: "#e0f2fe" },
            fontSize: chipFontSize,
          }}
        />
      ))}
      {remainingCount > 0 && (
        <Chip
          label={`+${remainingCount} more`}
          size="small"
          onClick={() => openDialog("All Skills", allSkillsFormatted)}
          sx={{
            backgroundColor: "#e5e7eb",
            color: "#4b5563",
            "&:hover": { backgroundColor: "#d1d5db" },
            cursor: "pointer",
            fontSize: chipFontSize,
          }}
        />
      )}
    </Box>
  );
};

// Add helper function to calculate years of experience from date ranges
const calculateYearsOfExperience = (experiences) => {
  if (!experiences) return 0;
  
  let totalYears = 0;
  const parsedExperiences = parseExperiences(experiences);
  
  // Ensure parsedExperiences is an array before using forEach
  if (!Array.isArray(parsedExperiences)) {
    console.warn('parsedExperiences is not an array:', parsedExperiences);
    return 0;
  }
  
  parsedExperiences.forEach(exp => {
    if (exp && exp.date_range) {
      const dateRange = exp.date_range.replace(/\s+/g, ' ').trim();
      const dates = dateRange.split('-');
      if (dates.length === 2) {
        const startDateStr = dates[0].trim();
        const endDateStr = dates[1].trim();
        
        // Parse dates with different formats
        const parseDate = (dateStr) => {
          // Try parsing as "Month Year" format
          const months = {
            'january': 0, 'february': 1, 'march': 2, 'april': 3, 'may': 4, 'june': 5,
            'july': 6, 'august': 7, 'september': 8, 'october': 9, 'november': 10, 'december': 11
          };
          
          const parts = dateStr.toLowerCase().split(' ');
          if (parts.length === 2 && months[parts[0]] !== undefined) {
            return new Date(parseInt(parts[1]), months[parts[0]], 1);
          }
          
          // Try parsing as "YYYY-MM-DD" format
          const date = new Date(dateStr);
          if (!isNaN(date.getTime())) {
            return date;
          }
          
          return null;
        };

        const startDate = parseDate(startDateStr);
        const endDate = parseDate(endDateStr);

        if (startDate && endDate) {
          const years = (endDate - startDate) / (1000 * 60 * 60 * 24 * 365);
          if (!isNaN(years) && years > 0) {
            totalYears += years;
          }
        }
      }
    }
  });
  
  return Math.round(totalYears);
};

// Update parseExperiences function to handle the specific format
const parseExperiences = (experiences) => {
  if (!experiences) return [];
  
  try {
    // If it's already an array, return it
    if (Array.isArray(experiences)) return experiences;
    
    // If it's a string, try to parse it
    if (typeof experiences === 'string') {
      try {
        // First try to parse as JSON
        const parsed = JSON.parse(experiences);
        
        // Check if it's an object with EXPERIENCE field
        if (parsed && parsed.EXPERIENCE) {
          return Array.isArray(parsed.EXPERIENCE) ? parsed.EXPERIENCE : [parsed.EXPERIENCE];
        }
        
        // If it's an array, return it
        if (Array.isArray(parsed)) {
          return parsed;
        }
        
        // If it's an object but not in the expected format, try to convert it
        return [{
          text: JSON.stringify(parsed),
          date_range: ''
        }];
      } catch (e) {
        // If JSON parsing fails, try to parse as individual objects
        const cleanedStr = experiences
          .replace(/'/g, '"')  // Replace single quotes with double quotes
          .replace(/(\w+):/g, '"$1":'); // Add quotes to property names
        
        try {
          // Try to parse as JSON array
          const parsed = JSON.parse(cleanedStr);
          
          // Check if it's an object with EXPERIENCE field
          if (parsed && parsed.EXPERIENCE) {
            return Array.isArray(parsed.EXPERIENCE) ? parsed.EXPERIENCE : [parsed.EXPERIENCE];
          }
          
          return Array.isArray(parsed) ? parsed : [parsed];
        } catch (e) {
          // If that fails, try to parse as individual objects
          const objects = cleanedStr.split('}, {').map((obj, index) => {
            if (index === 0) obj = obj.replace('{', '');
            if (index === 1) obj = obj.replace('}', '');
            return `{${obj}}`;
          });
          
          return objects.map(obj => {
            try {
              const parsed = JSON.parse(obj);
              return parsed.EXPERIENCE || { text: obj, date_range: '' };
            } catch (e) {
              return { text: obj, date_range: '' };
            }
          });
        }
      }
    }
    
    // If we can't parse it, return as a single experience object
    return [{ text: String(experiences), date_range: '' }];
  } catch (error) {
    console.error('Error parsing experiences:', error);
    return [{ text: String(experiences), date_range: '' }];
  }
};

// Add truncateExperienceText function
const truncateExperienceText = (text) => {
  if (typeof text !== 'string') return text;
  
  const windowWidth = window.innerWidth;
  let maxLength;
  
  if (windowWidth <= 1920) { // 1080p
    maxLength = 9;
  } else if (windowWidth <= 2560) { // 1440p
    maxLength = 11;
  } else {
    maxLength = 13;
  }
  
  return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
};

const ViewToggleButton = styled(Button)(({ theme }) => ({
  borderRadius: "14px",
  textTransform: "none",
  fontWeight: 600,
  padding: "12px 24px",
  fontSize: "0.95rem",
  background: "linear-gradient(135deg, #0017E7 0%, rgba(0, 23, 231, 0.8) 100%)",
  color: "#ffffff",
  border: "none",
  boxShadow: "0 6px 20px rgba(0, 23, 231, 0.25)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    background: "linear-gradient(135deg, rgba(0, 23, 231, 0.9) 0%, #0017E7 100%)",
    transform: "translateY(-2px)",
    boxShadow: "0 8px 25px rgba(0, 23, 231, 0.35)",
  }
}));

const Blacklist = ({ data, viewType, setViewType, onDataUpdate }) => {
  const [blacklistData, setBlacklistData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError, setDataError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogContent, setDialogContent] = useState("");
  const [scale, setScale] = useState(1);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [actionLoading, setActionLoading] = useState({});
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [candidateToUnblock, setCandidateToUnblock] = useState(null);

  // Add state for experience dialog
  const [moreItemsDialogOpen, setMoreItemsDialogOpen] = useState(false);
  const [moreItemsDialogTitle, setMoreItemsDialogTitle] = useState("");
  const [moreItemsDialogContent, setMoreItemsDialogContent] = useState([]);

  useEffect(() => {
    const handleResize = () => {
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;
      const contentHeight = 800; // Approximate height of your content
      const contentWidth = 1200; // Approximate width of your content
      
      // Calculate scale based on both height and width
      const heightScale = (windowHeight * 0.95) / contentHeight; // 95% of window height
      const widthScale = (windowWidth * 0.95) / contentWidth; // 95% of window width
      
      // Use the smaller scale to ensure content fits both dimensions
      let newScale = Math.min(heightScale, widthScale, 1);
      
      // Add minimum scale limit based on screen width
      if (windowWidth <= 1920) { // For 1080p and similar resolutions
        newScale = Math.max(newScale, 0.8); // Minimum 80% scale
      } else if (windowWidth <= 2560) { // For 1440p
        newScale = Math.max(newScale, 0.85); // Minimum 85% scale
      } else {
        newScale = Math.max(newScale, 0.9); // Minimum 90% scale for larger screens
      }
      
      setScale(newScale);
    };

    handleResize(); // Initial scale
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (data) {
      setBlacklistData(data);
      setFilteredData(data);
      setDataLoading(false);
    }
  }, [data]);

  const getRecruiterId = () => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) return null;
    try {
      const base64Url = accessToken.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      return JSON.parse(jsonPayload).user_id;
    } catch (error) {
      console.error("Failed to parse JWT token", error);
      return null;
    }
  };
  const fetchBlacklistedCandidates = async () => {
    setDataLoading(true);
    setDataError(null);
    const recruiterId = getRecruiterId();

    if (!recruiterId) {
      setDataError("Authentication error. Please log in again.");
      setDataLoading(false);
      return;
    }

    try {
      const response = await axios.get(
        `${apiUrl}authentication/blacklist/create/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          params: { recruiter_id: recruiterId },
        }
      );

      if (response.data?.status === "success") {
        const normalizedData = response.data.blacklisted_candidates.map(
          (item) => ({
            ...item.candidate,
            blacklist_id: item.id, // Store blacklist entry ID separately
            candidate_id: item.candidate_id,
            reason: item.reason,
            date_blacklisted: item.date_blacklisted,
          })
        );
        setBlacklistData(normalizedData || []);
      } else {
        throw new Error(response.data?.message || "Failed to fetch data");
      }
    } catch (error) {
      console.error("Fetch error:", error);
      setDataError(
        error.response?.data?.message ||
          error.message ||
          "Failed to load blacklist"
      );
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    fetchBlacklistedCandidates();
  }, []);

const handleSearch = (searchResults) => {
  console.log("Search results:", searchResults); // Debug: check what's coming in

  const normalizedResults = searchResults.map((item) => {
    // First determine if we're dealing with a blacklist entry that has a nested candidate
    const hasNestedCandidate =
      item.candidate && typeof item.candidate === "object";

    // Extract candidate and metadata based on structure
    const candidateData = hasNestedCandidate ? item.candidate : item;
    const blacklistId = item.id;
    const candidateId = item.candidate_id || candidateData.id;

    return {
      ...candidateData,
      blacklist_id: blacklistId,
      candidate_id: candidateId,
      reason:
        item.reason ||
        (hasNestedCandidate ? item.reason : candidateData.reason),
      date_blacklisted: item.date_blacklisted,
    };
  });

  setFilteredData(normalizedResults);
};
  const openDialog = (title, content) => {
    setDialogTitle(title);
    setDialogContent(
      typeof content === "string" ? content.replace(/[\[\]']+/g, "") : content
    );
    setDialogOpen(true);
  };

 const confirmUnblock = (candidateData, candidateName) => {
   // First try to get the candidate_id directly
   let candidateId = candidateData.candidate_id;

   // If it's undefined, fall back to the id property
   if (candidateId === undefined) {
     candidateId = candidateData.id;
   }

   console.log("Using candidate ID:", candidateId);
   setCandidateToUnblock({ id: candidateId, name: candidateName });
   setConfirmDialogOpen(true);
 };

  const handleUnblock = async () => {
    if (!candidateToUnblock) return;
    const candidateId = candidateToUnblock.id;
    console.log("the candidate id", candidateId);
    const recruiterId = getRecruiterId();

    setActionLoading((prev) => ({ ...prev, [candidateId]: true }));

    try {
      const response = await axios.delete(
        `${apiUrl}authentication/blacklist/create/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          data: {
            recruiter_id: recruiterId,
            candidate_id: candidateId,
          },
        }
      );

      if (response.status === 200 || response.status === 204) {
        setAlert({
          open: true,
          message: "Candidate removed from blacklist",
          severity: "success",
        });
        setBlacklistData((prev) =>
          prev.filter((item) => item.id !== candidateId)
        );
        fetchBlacklistedCandidates();
      }
    } catch (error) {
      setAlert({
        open: true,
        message: error.response?.data?.message || "Unblock failed",
        severity: "error",
      });
    } finally {
      setActionLoading((prev) => ({ ...prev, [candidateId]: false }));
      setConfirmDialogOpen(false);
      setCandidateToUnblock(null);
    }
  };

  // Add handleShowMore function
  const handleShowMore = (title, items) => {
    setMoreItemsDialogTitle(title);
    
    // For experiences, we know it's already an array of objects with text and date_range
    if (title === "Experience" && Array.isArray(items)) {
      setMoreItemsDialogContent(items.map(item => ({
        type: 'experience',
        text: item.text || '',
        date_range: item.date_range || ''
      })));
    } else {
      // For other types of content
      const parsedItems = Array.isArray(items) ? items : [items];
      setMoreItemsDialogContent(parsedItems.map(item => {
        if (typeof item === 'string' && item.includes('LinkedIn:')) {
          const linkedInUrl = item.replace('LinkedIn:', '').trim();
          return {
            type: 'link',
            label: 'LinkedIn Profile',
            href: linkedInUrl.startsWith("http") ? linkedInUrl : `https://www.linkedin.com/in/${linkedInUrl}`
          };
        }
        return item;
      }));
    }
    setMoreItemsDialogOpen(true);
  };

  return (
    <Box sx={{ 
      transform: `scale(${scale})`,
      transformOrigin: 'top left',
      width: `${100 / scale}%`,
      height: `${100 / scale}%`,
      transition: 'transform 0.3s ease-in-out'
    }}>
      <Paper elevation={0} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          {viewType !== "card" ? (
            <ViewToggleButton
              onClick={setViewType}
              startIcon={<ViewModuleIcon />}
              sx={{ height: 50 }}
            >
              Card View
            </ViewToggleButton>
          ) : (
            <Button
              variant="outlined"
              onClick={setViewType}
              startIcon={<ViewListIcon />}
              sx={{ height: 50 }}
            >
              List View
            </Button>
          )}
          <Box sx={{ flex: 1 }}>
            <ApplicationSearchBar
              data={blacklistData}
              onSearch={handleSearch}
              SEARCH_API_ENDPOINT={`${apiUrl}candidates/api/blacklist/search/`}
              AUTOCOMPLETE_API_ENDPOINT={`${apiUrl}candidates/api/blacklist/autocomplete/`}
              location={"location"}
            />
          </Box>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, backgroundColor: "#f8fafc" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <User size={16} />
                    Applicant
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: "#f8fafc" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Phone size={16} />
                    Contact
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: "#f8fafc" }}>
                  Email
                </TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: "#f8fafc" }}>
                  Location
                </TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: "#f8fafc" }}>
                  Skills
                </TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: "#f8fafc" }}>
                  Education
                </TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: "#f8fafc" }}>
                  Experience
                </TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: "#f8fafc" }}>
                  Profile
                </TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: "#f8fafc" }}>
                  Reason
                </TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: "#f8fafc" }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {dataLoading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={40} />
                  </TableCell>
                </TableRow>
              ) : dataError ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                    <Typography color="error">{dataError}</Typography>
                  </TableCell>
                </TableRow>
              ) : filteredData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                    <Typography color="text.secondary">
                      No blacklisted candidates found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredData.map((row) => (
                  <TableRow
                    key={row.id}
                    sx={{ "&:hover": { backgroundColor: "#f8fafc" } }}
                  >
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontSize: "1.1rem" }}>{row.first_name} {row.last_name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: "1.1rem" }}>{row.phone || "-"}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: "1.1rem" }}>{row.email || "-"}</Typography>
                    </TableCell>
                    <TableCell>
                      {row.city || "-"}
                      {row.address && (
                        <Typography variant="caption" display="block">
                          <TruncatedContent
                            content={row.address}
                            maxLength={15}
                            openDialog={openDialog}
                          />
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <SkillChips skills={row.skills} openDialog={openDialog} chipFontSize="1rem" />
                    </TableCell>
                    <TableCell>
                      <TruncatedContent
                        content={row.educations}
                        openDialog={openDialog}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        gap: { xs: 0.5, sm: 0.5 }
                      }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 0.5
                        }}>
                          {(() => {
                            const yearsOfExperience = calculateYearsOfExperience(row.experiences);
                            const parsedExperiences = parseExperiences(row.experiences);
                            
                            return (
                              <>
                                <Chip
                                  label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      {yearsOfExperience >= 1 && (
                                        <Typography variant="body1" sx={{ fontSize: '1rem', color: 'text.secondary' }}>
                                          {truncateExperienceText('+')}
                                        </Typography>
                                      )}
                                      <Typography variant="body1" sx={{ fontSize: '1rem' }}>
                                        <strong>
                                          {yearsOfExperience < 1 
                                            ? 'Less than 1 year' 
                                            : `${yearsOfExperience} years`}
                                        </strong>
                                      </Typography>
                                    </Box>
                                  }
                                  sx={{
                                    height: 'auto',
                                    '& .MuiChip-label': {
                                      display: 'flex',
                                      alignItems: 'center',
                                      whiteSpace: 'normal',
                                      padding: '8px 12px'
                                    }
                                  }}
                                />
                                {parsedExperiences.length > 0 && (
                                  <Button
                                    variant="text"
                                    color="primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleShowMore("Experience", parsedExperiences);
                                    }}
                                    sx={{ 
                                      p: '2px 4px', 
                                      minHeight: 0, 
                                      lineHeight: 'normal', 
                                      textTransform: "none", 
                                      fontSize: '0.85rem',
                                      minWidth: 'auto',
                                      ml: 0.2,
                                      '&:hover': {
                                        backgroundColor: 'transparent',
                                        color: 'primary.dark'
                                      }
                                    }}
                                    size="small"
                                  >
                                    See More
                                  </Button>
                                )}
                              </>
                            );
                          })()}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <TruncatedContent
                        content={row.profile}
                        openDialog={openDialog}
                      />
                    </TableCell>
                    <TableCell>
                      <TruncatedContent
                        content={row.reason}
                        maxLength={15}
                        openDialog={() => openDialog("Reason", row.reason)}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={
                          actionLoading[row.id] ? (
                            <CircularProgress size={16} />
                          ) : (
                            <UserPlus size={16} />
                          )
                        }
                        onClick={() => {
                          console.log("Row data:", row);
                          confirmUnblock(
                            row,
                            `${row.first_name} ${row.last_name}`
                          );
                        }}
                        disabled={actionLoading[row.id]}
                      >
                        Unblock
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
      >
        <DialogTitle sx={{ fontSize: "1.3rem" }}>{dialogTitle}</DialogTitle>
        <DialogContent>
          <pre style={{ whiteSpace: "pre-wrap", fontSize: "1.1rem" }}>
            {typeof dialogContent === 'string' ? dialogContent : JSON.stringify(dialogContent)}
          </pre>
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
      >
        <DialogTitle sx={{ fontSize: "1.3rem" }}>Confirm Unblock</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "1.1rem" }}>
            Remove {candidateToUnblock?.name} from blacklist?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} sx={{ fontSize: "1.1rem" }}>Cancel</Button>
          <Button
            onClick={handleUnblock}
            color="primary"
            variant="contained"
            disabled={actionLoading[candidateToUnblock?.id]}
            sx={{ fontSize: "1.1rem" }}
          >
            {actionLoading[candidateToUnblock?.id] ? (
              <CircularProgress size={24} />
            ) : (
              "Confirm"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add the "Show More" Dialog */}
      <Dialog
        open={moreItemsDialogOpen}
        onClose={() => setMoreItemsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="h5" component="div" sx={{ fontWeight: 600, color: '#333' }} >
            {moreItemsDialogTitle}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={() => setMoreItemsDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: '#666'
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ padding: 0 }}>
          {moreItemsDialogTitle === "Experience" ? (
            <Box sx={{ maxHeight: '70vh', padding: 3 }}>
              {moreItemsDialogContent.map((experience, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: 3,
                    p: 2.5,
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    backgroundColor: index % 2 === 0 ? '#f9fafb' : '#ffffff',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.08)'
                    }
                  }}
                >
                  <Typography
                    variant="body1"
                    component="div"
                    sx={{
                      color: '#424242',
                      fontSize: '1rem',
                      fontWeight: 500,
                      lineHeight: 1.6,
                      whiteSpace: 'pre-line'
                    }}
                  >
                    {experience.text}
                  </Typography>

                  {experience.date_range && (
                    <Typography
                      variant="body2"
                      component="div"
                      sx={{
                        color: '#757575',
                        fontSize: '0.875rem',
                        fontStyle: 'italic',
                        mt: 1
                      }}
                    >
                      {experience.date_range}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, p: 2 }}>
              {moreItemsDialogContent.map((item, i) => {
                if (typeof item === 'object' && item.type === 'link') {
                  return (
                    <Chip
                      key={i}
                      label={item.label}
                      component="a"
                      href={item.href}
                      target="_blank"
                      clickable
                      sx={{
                        margin: "4px",
                        "& .MuiChip-label": {
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          padding: "12px 16px",
                          fontSize: "1rem",
                          lineHeight: "1.5",
                        },
                      }}
                    />
                  );
                }
                return (
                  <Chip
                    key={i}
                    label={typeof item === 'string' ? item : JSON.stringify(item)}
                    sx={{
                      margin: "4px",
                      "& .MuiChip-label": {
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        padding: "12px 16px",
                        fontSize: "1rem",
                        lineHeight: "1.5",
                      },
                    }}
                  />
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: 2, borderTop: '1px solid #e0e0e0', justifyContent: 'flex-end' }}>
          <Button
            onClick={() => setMoreItemsDialogOpen(false)}
            color="primary"
            variant="contained"
            sx={{
              px: 3,
              py: 1,
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={alert.severity} sx={{ width: "100%" }}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Blacklist;